import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useAuth } from '../../contexts/AuthContext';
import { updateUser, resetUserPassword } from '../../lib/api';
import { toast } from 'sonner';
import { Settings, User, Mail, Shield, KeyRound } from 'lucide-react';

export default function AdminSettingsPage() {
  const { user, refreshUser } = useAuth();
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    setEmail(user?.email || '');
  }, [user?.email]);

  const handleEmailChange = async () => {
    const nextEmail = email.trim();
    if (!user?.user_id) return;
    if (!nextEmail) {
      toast.error('Email is required');
      return;
    }
    if (nextEmail === user.email) {
      toast.info('Email is unchanged');
      return;
    }

    setSavingEmail(true);
    try {
      await updateUser(user.user_id, { email: nextEmail });
      await refreshUser?.();
      toast.success('Email updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!user?.user_id) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSavingPassword(true);
    try {
      await resetUserPassword(user.user_id, newPassword);
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Admin Settings</h1>
        <p className="text-slate-500">Your admin account and access information.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-indigo-600" />
            Account Details
          </CardTitle>
          <CardDescription>
            Basic profile details for the currently signed-in admin.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <User className="w-4 h-4 text-slate-500" />
            <span className="font-medium min-w-20">Name:</span>
            <span>{user?.name || '-'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <Mail className="w-4 h-4 text-slate-500" />
            <span className="font-medium min-w-20">Email:</span>
            <span>{user?.email || '-'}</span>
          </div>
          <div className="flex items-center gap-3 text-sm text-slate-700">
            <Shield className="w-4 h-4 text-slate-500" />
            <span className="font-medium min-w-20">Role:</span>
            <span className="uppercase">{user?.role || 'admin'}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-600" />
            Change Email
          </CardTitle>
          <CardDescription>Update the login email for this admin account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 max-w-lg">
            <Label htmlFor="admin-email">New Email</Label>
            <Input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@company.com"
              data-testid="admin-settings-email-input"
            />
          </div>
          <Button
            onClick={handleEmailChange}
            disabled={savingEmail}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="admin-settings-save-email-btn"
          >
            {savingEmail ? 'Saving...' : 'Save Email'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-indigo-600" />
            Change Password
          </CardTitle>
          <CardDescription>Set a new password for this admin account.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-2 max-w-lg">
            <Label htmlFor="admin-new-password">New Password</Label>
            <Input
              id="admin-new-password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              data-testid="admin-settings-new-password-input"
            />
          </div>
          <div className="grid gap-2 max-w-lg">
            <Label htmlFor="admin-confirm-password">Confirm New Password</Label>
            <Input
              id="admin-confirm-password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              data-testid="admin-settings-confirm-password-input"
            />
          </div>
          <Button
            onClick={handlePasswordChange}
            disabled={savingPassword}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="admin-settings-save-password-btn"
          >
            {savingPassword ? 'Updating...' : 'Update Password'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
